// src/oura/components/docs/write/MultiEmailDialog.tsx
//
// "Send Email" on the Documents board (3 Nov 2026): one client email for several
// docs. Staff pick what it is for and which docs it links (each shared with the
// client, a Google Doc or a PDF, up to 6); Oura writes one email naming each doc
// with its codes, a line on what it covers and its link, the docs' highlights and
// asks together, the reply-by and the sign-off (ourteam-backend, client-email/multi).
//
// Staff can edit it, send a test, and send it. Nothing they type is kept: an edit
// lives here until the email is sent or the dialog is closed (ops' choice; the
// single doc's email keeps its edits).

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FlaskConicalIcon, Loader2Icon, MailIcon, PencilLineIcon, SendIcon, XIcon } from "lucide-react";
import {
    draftMultiClientEmail,
    previewMultiClientEmail,
    sendMultiClientEmail,
    sendMultiClientEmailTest,
    type ClientEmailPurpose,
    type MultiClientEmailDraft,
} from "@service/aeo/clientDocsWrite";
import type { StaffDocument } from "@service/aeo/clientDocs";
import { PillButton, Seg, TYPE, Tag } from "@oura/ui";
import { cn } from "../../../lib/cn";
import { docsFailureText, staffDocTitle } from "@oura/docs";
import { formatPeople } from "../../../onboarding/drafts";
import { readRecipients, toMessageText, type MessageText } from "../../../onboarding/messageText";
import { Button } from "../../../primitives/Button";
import { EditClientEmailDialog } from "./EditClientEmailDialog";
import { PreviewFrame } from "./ClientEmailView";
import { SendClientEmailDialog } from "./SendClientEmailDialog";
import { TestClientEmailDialog } from "./TestClientEmailDialog";

const MAX_DOCS = 6;
const ID = "multi-email";
// "Send monthly report" is hidden (ops, 3 Nov 2026); the server still takes it.
const PURPOSES: { key: ClientEmailPurpose; label: string }[] = [
    { key: "review", label: "Send for review" },
    { key: "follow_up", label: "Follow up" },
];

/** A doc this email can link: a client Google Doc or PDF the client can open. */
function canLinkInEmail(doc: StaffDocument): boolean {
    return !doc.notClientDoc && (doc.fileType === "gdoc" || doc.fileType === "pdf") && doc.sharing.clientCanSee;
}

export function MultiEmailDialog({ businessId, documents, onClose }: { businessId: string; documents: StaffDocument[]; onClose: () => void }) {
    const linkable = useMemo(() => documents.filter(canLinkInEmail), [documents]);
    const notShared = documents.filter((d) => !d.notClientDoc && (d.fileType === "gdoc" || d.fileType === "pdf") && !d.sharing.clientCanSee).length;
    const [picked, setPicked] = useState<string[]>([]);
    const [purpose, setPurpose] = useState<ClientEmailPurpose>("review");
    const [draft, setDraft] = useState<MultiClientEmailDraft | null>(null);
    const [drafting, setDrafting] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const [text, setText] = useState<MessageText | null>(null);
    const [html, setHtml] = useState("");
    const [previewing, setPreviewing] = useState(false);
    const [previewFailed, setPreviewFailed] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testFailed, setTestFailed] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendFailed, setSendFailed] = useState<string | null>(null);
    const [unconfirmed, setUnconfirmed] = useState(false);
    const [note, setNote] = useState<string | null>(null);
    const [sent, setSent] = useState<string | null>(null);
    const sendKey = useRef<string | null>(null);

    // The preview follows the edit (nothing is kept), and Send waits for it: staff send
    // what they last saw (Jack, #4911). Back to Oura's email, Oura's own render.
    useEffect(() => {
        if (!text || !draft) return;
        if (text.subject === draft.subject && text.body === draft.body) {
            setHtml(draft.html);
            setPreviewing(false);
            setPreviewFailed(null);
            return;
        }
        let alive = true;
        setPreviewing(true);
        setPreviewFailed(null);
        void previewMultiClientEmail(businessId, { subject: text.subject, body: text.body }).then((r) => {
            if (!alive) return;
            setPreviewing(false);
            if (!r.ok) return setPreviewFailed(docsFailureText(r, "the preview"));
            setHtml(r.data.html);
        });
        return () => {
            alive = false;
        };
    }, [text, draft, businessId]);

    const toggle = (id: string) =>
        setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= MAX_DOCS ? p : [...p, id]));

    const write = async () => {
        setDrafting(true);
        setFailed(null);
        const r = await draftMultiClientEmail(businessId, { documentIds: picked, purpose });
        setDrafting(false);
        if (!r.ok) return setFailed(docsFailureText(r, "the email"));
        setDraft(r.data);
        setText(toMessageText({ to: r.data.to, cc: r.data.cc, subject: r.data.subject, body: r.data.body }));
        setHtml(r.data.html);
        sendKey.current = crypto.randomUUID();
    };

    const people = text ? readRecipients(text) : null;
    const cancel = () => !sending && !drafting && onClose();

    const sendTest = async () => {
        if (!text) return;
        setTesting(true);
        setTestFailed(null);
        const r = await sendMultiClientEmailTest(businessId, { subject: text.subject, body: text.body });
        setTesting(false);
        if (!r.ok) return setTestFailed(docsFailureText(r, "the test"));
        setTestOpen(false);
        setNote(`Test sent to ${r.data.to}.`);
    };

    const send = async (resendUnknown: boolean) => {
        if (!text || !draft || !people?.draft) return;
        if (resendUnknown || !sendKey.current) sendKey.current = crypto.randomUUID();
        setSending(true);
        setSendFailed(null);
        const r = await sendMultiClientEmail(businessId, {
            documentIds: draft.documents.map((d) => d.id),
            purpose: draft.purpose,
            to: people.draft.to,
            cc: people.draft.cc,
            subject: text.subject,
            body: text.body,
            sendKey: sendKey.current ?? "",
            resendUnknown,
        });
        setSending(false);
        if (!r.ok) {
            setUnconfirmed(r.reason === "rejected" && r.code === "send_unconfirmed");
            return setSendFailed(docsFailureText(r, "the email"));
        }
        setConfirming(false);
        setSent(`Sent to ${formatPeople(people.draft.to)}.`);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${ID}-title`}
            data-multi-email-dialog
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape" && !editing && !testOpen && !confirming) cancel();
            }}
        >
            <div className="absolute inset-0 bg-gray-900/50" onClick={cancel} />
            <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
                <div className="flex items-start gap-3 border-b border-gray-100 px-6 py-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50">
                        <MailIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5">
                        <h2 id={`${ID}-title`} className="m-0 text-base font-semibold text-gray-900">
                            Email the client
                        </h2>
                        <p className="m-0 mt-0.5 text-sm text-gray-500">One email with a link to each document you pick.</p>
                    </div>
                    <button type="button" onClick={cancel} aria-label="Close" className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                    {sent ? (
                        <p className="m-0 text-sm text-gray-900">{sent}</p>
                    ) : !draft ? (
                        <>
                            <div>
                                <p className={cn(TYPE.tm, "m-0 mb-2 text-gray-600")}>What it is for</p>
                                <Seg<ClientEmailPurpose> size="sm" ariaLabel="What it is for" value={purpose} onChange={setPurpose} className="flex-wrap" items={PURPOSES} />
                            </div>
                            <div>
                                <p className={cn(TYPE.tm, "m-0 mb-2 text-gray-600")}>
                                    Documents to link <span className="text-gray-400">({picked.length} of {MAX_DOCS} at most)</span>
                                </p>
                                {linkable.length === 0 ? (
                                    <p className="m-0 text-sm text-gray-500">No document is shared with the client yet. Share one first, so its link opens.</p>
                                ) : (
                                    <ul className="m-0 list-plain divide-y divide-gray-100 rounded-lg border border-gray-200 p-0">
                                        {linkable.map((d) => {
                                            const on = picked.includes(d.id);
                                            const full = !on && picked.length >= MAX_DOCS;
                                            return (
                                                <li key={d.id}>
                                                    <label className={cn("flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-gray-50", full && "cursor-not-allowed opacity-50")}>
                                                        <input type="checkbox" className="h-4 w-4" checked={on} disabled={full} onChange={() => toggle(d.id)} />
                                                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{staffDocTitle(d)}</span>
                                                        <span className="flex flex-shrink-0 items-center gap-1">
                                                            {d.codes.map((c) => (
                                                                <Tag key={c} tone="blue">
                                                                    {c}
                                                                </Tag>
                                                            ))}
                                                            <Tag tone="gray">{d.fileType === "pdf" ? "PDF" : "Google Doc"}</Tag>
                                                        </span>
                                                    </label>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                {notShared > 0 && (
                                    <p className="m-0 mt-2 text-xs text-gray-500">
                                        {notShared === 1 ? "1 document is" : `${notShared} documents are`} not shared with the client yet, so not listed.
                                    </p>
                                )}
                            </div>
                            {failed && (
                                <p role="alert" className="m-0 text-sm text-rose-600">
                                    {failed}
                                </p>
                            )}
                        </>
                    ) : (
                        text && (
                            <>
                                <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                                    To {people?.draft ? formatPeople(people.draft.to) : text.to || "nobody yet"}
                                    {people?.draft && people.draft.cc.length > 0 ? ` · Copy ${formatPeople(people.draft.cc)}` : ""}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <PillButton size="sm" icon={FlaskConicalIcon} onClick={() => setTestOpen(true)}>
                                        Send a test
                                    </PillButton>
                                    <PillButton size="sm" icon={PencilLineIcon} onClick={() => setEditing(true)}>
                                        Edit email
                                    </PillButton>
                                </div>
                                {draft.unread.length > 0 && (
                                    <p className="m-0 text-xs text-gray-500">
                                        Oura could not read {draft.unread.length === 1 ? "1 of the documents" : `${draft.unread.length} of the documents`} (a PDF, say), so
                                        its highlights and asks are not in the email. Add them in Edit email if needed.
                                    </p>
                                )}
                                {previewFailed && (
                                    <p role="alert" className="m-0 text-sm text-rose-600">
                                        {previewFailed} Open Edit email and save again to retry.
                                    </p>
                                )}
                                <PreviewFrame html={html} subject={text.subject} />
                                {note && <p className="m-0 text-sm text-gray-600">{note}</p>}
                                <p className="m-0 text-xs text-gray-500">Edits are not kept: close this and they go.</p>
                            </>
                        )
                    )}
                </div>

                <div className="flex gap-2 border-t border-gray-100 px-6 py-4">
                    {sent ? (
                        <Button className="flex-1" onClick={onClose}>
                            Done
                        </Button>
                    ) : !draft ? (
                        <>
                            <Button type="button" variant="outline" className="flex-1" disabled={drafting} onClick={cancel}>
                                Cancel
                            </Button>
                            <Button type="button" className="flex-1" disabled={drafting || picked.length === 0} onClick={() => void write()}>
                                {drafting ? (
                                    <>
                                        <Loader2Icon className="h-4 w-4 animate-spin" />
                                        Writing the email…
                                    </>
                                ) : (
                                    <>
                                        <MailIcon className="h-4 w-4" />
                                        Write the email
                                    </>
                                )}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                disabled={sending}
                                onClick={() => {
                                    setDraft(null);
                                    setText(null);
                                    setNote(null);
                                }}
                            >
                                Back
                            </Button>
                            <Button type="button" className="flex-1" disabled={sending || previewing || Boolean(previewFailed) || !people?.draft} onClick={() => setConfirming(true)}>
                                {previewing ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
                                Send to the client
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {editing && text && (
                <EditClientEmailDialog
                    docId={ID}
                    value={text}
                    html={html}
                    onSave={(next) => {
                        setText(next);
                        setEditing(false);
                    }}
                    onClose={() => setEditing(false)}
                />
            )}
            <TestClientEmailDialog
                open={testOpen}
                docId={ID}
                subject={text?.subject ?? ""}
                sending={testing}
                failed={testFailed}
                onSend={() => void sendTest()}
                onClose={() => setTestOpen(false)}
            />
            <SendClientEmailDialog
                open={confirming}
                docId={ID}
                to={people?.draft?.to ?? []}
                cc={people?.draft?.cc ?? []}
                subject={text?.subject ?? ""}
                sending={sending}
                failed={sendFailed}
                unconfirmed={unconfirmed}
                onSend={(resend) => void send(resend)}
                onClose={() => setConfirming(false)}
            />
        </div>,
        document.body,
    );
}
